import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  useTablaPrefs,
  ResizeHandle,
  ColorMenu,
  colorPersonal,
  aplicarColor,
  type MenuColor,
} from '@/lib/uiPrefs';

/**
 * "Comunicaciones CxC" (7-oct-2026, pedido de Fer): bitácora de gestiones de
 * cobranza, réplica del Sheet. Carga inicial desde el Sheet (refresco por
 * corrida) + captura/edición directa en Atlas: lo editado/creado aquí manda
 * y el refresco no lo pisa. Editan: cobranza, directores y admin; supervisor
 * solo lectura.
 */

interface Comunicacion {
  id: number;
  origen: string;
  fecha: string | null;
  horario: string | null;
  ejecutiva: string | null;
  cliente: string | null;
  via: string | null;
  actividad: string | null;
  respuesta: string | null;
  fechaProgramada: string | null;
  monto: string | null;
  moneda: string | null;
  cumplio: string | null;
  editadoPor: string | null;
  [k: string]: unknown;
}

const COLS: { key: string; label: string; ancho?: string }[] = [
  { key: 'fecha', label: 'Fecha' },
  { key: 'horario', label: 'Horario' },
  { key: 'ejecutiva', label: 'Ejecutiva de cobranza' },
  { key: 'cliente', label: 'Cliente' },
  { key: 'via', label: 'Vía de comunicación' },
  { key: 'actividad', label: 'Actividad realizada', ancho: 'min-w-[20rem]' },
  { key: 'respuesta', label: 'Respuesta del cliente', ancho: 'min-w-[16rem]' },
  { key: 'fechaProgramada', label: 'Fecha programada' },
  { key: 'monto', label: 'Monto programado' },
  { key: 'moneda', label: 'Moneda' },
  { key: 'cumplio', label: '¿Cumplió con el compromiso?' },
];

function Celda({
  fila,
  campo,
  editable,
}: {
  fila: Comunicacion;
  campo: string;
  editable: boolean;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [valor, setValor] = useState('');
  const mut = useMutation({
    mutationFn: (v: string) =>
      api(`/cxc/comunicaciones/${fila.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ [campo]: v }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cxc-comunicaciones'] }),
    onError: (err: any) =>
      toast.error(err instanceof ApiError ? err.message : 'No se pudo guardar'),
  });
  const actual = fila[campo] == null ? '' : String(fila[campo]);
  if (!editing) {
    return (
      <div
        className={cn(
          'min-h-[1.4rem] truncate whitespace-pre-wrap',
          editable && 'cursor-text hover:outline hover:outline-1 hover:outline-amber-500 rounded',
        )}
        title={actual || (editable ? 'Click para editar' : undefined)}
        onClick={() => {
          if (!editable) return;
          setValor(actual);
          setEditing(true);
        }}
      >
        {actual || (editable ? <span className="text-ink-300">—</span> : '')}
      </div>
    );
  }
  const guardar = () => {
    setEditing(false);
    if (valor !== actual) mut.mutate(valor);
  };
  return (
    <input
      autoFocus
      className="w-full min-w-[6rem] px-1 py-0.5 text-sm border border-brand-400 rounded bg-surface-1"
      value={valor}
      onChange={(e) => setValor(e.target.value)}
      onBlur={guardar}
      onKeyDown={(e) => {
        if (e.key === 'Enter') guardar();
        if (e.key === 'Escape') setEditing(false);
      }}
    />
  );
}

export function ComunicacionesCxcPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [ejecutiva, setEjecutiva] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [uiPrefs, setUiPrefs] = useTablaPrefs('comunicaciones-cxc');
  const [menuColor, setMenuColor] = useState<MenuColor | null>(null);
  const setW = (k: string, w: number) => setUiPrefs((p) => ({ ...p, w: { ...p.w, [k]: w } }));

  const q = useQuery({
    queryKey: ['cxc-comunicaciones', search, ejecutiva, page, perPage],
    queryFn: () =>
      api<{
        items: Comunicacion[];
        total: number;
        totalPages: number;
        ejecutivas: string[];
      }>('/cxc/comunicaciones', {
        params: { search: search || undefined, ejecutiva, page, perPage },
      }),
  });
  const totalPages = q.data?.totalPages ?? 1;

  const crear = useMutation({
    mutationFn: () => {
      const ahora = new Date();
      return api('/cxc/comunicaciones', {
        method: 'POST',
        body: JSON.stringify({
          fecha: ahora.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }),
          horario: ahora.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false }),
        }),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cxc-comunicaciones'] });
      toast.success('Registro agregado — llena las celdas con un click');
    },
    onError: (err: any) =>
      toast.error(err instanceof ApiError ? err.message : 'No se pudo agregar'),
  });

  const editable =
    user?.rol === 'admin' || user?.rol === 'director' || user?.rol === 'cobranza';
  const items = q.data?.items ?? [];

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-6 space-y-4 w-[95%] max-w-none mx-auto">
      <style>{`
        .cxcc-scroll { overflow: scroll; max-height: 72vh; }
        .cxcc-scroll::-webkit-scrollbar { width: 14px; height: 14px; }
        .cxcc-scroll::-webkit-scrollbar-track { background: #f1f5f9; }
        .cxcc-scroll::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 7px; border: 3px solid #f1f5f9; }
      `}</style>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-lg font-semibold">Comunicaciones CxC</h1>
          <p className="text-sm text-ink-400">
            Bitácora de gestiones de cobranza. Todas las celdas se editan con un click; lo
            capturado aquí manda sobre el Sheet. {q.data ? `${q.data.total} registros.` : ''}
          </p>
        </div>
        {editable && (
          <Button size="sm" onClick={() => crear.mutate()} disabled={crear.isPending}>
            <Plus className="h-4 w-4 mr-1" /> Agregar registro
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-surface-2">
        <Input
          placeholder="Buscar cliente o actividad…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="w-64"
        />
        <Select
          value={ejecutiva ?? '__all__'}
          onValueChange={(v) => {
            setEjecutiva(v === '__all__' ? undefined : v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-56">
            <SelectValue placeholder="Ejecutiva" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todas las ejecutivas</SelectItem>
            {(q.data?.ejecutivas ?? []).map((e) => (
              <SelectItem key={e} value={e!}>
                {e}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-ink-400 ml-auto">
          {q.data ? `${q.data.total} registros` : ''}
        </span>
      </div>

      <div className="cxcc-scroll rounded-lg border border-ink-200 bg-surface-1">
        <table
          className="text-sm"
          style={{ tableLayout: 'auto', width: 'max-content', minWidth: '100%', borderCollapse: 'separate', borderSpacing: 0 }}
        >
          <thead className="sticky top-0 z-10">
            <tr className="bg-surface-2 text-xs uppercase tracking-wider text-ink-400">
              {COLS.map((c) => (
                <th
                  key={c.key}
                  className={cn(
                    'px-2 py-2 text-left font-medium border-b border-r border-ink-100 whitespace-nowrap bg-surface-2 relative',
                    c.ancho,
                  )}
                  style={{
                    overflow: 'hidden',
                    ...(uiPrefs.w[c.key]
                      ? { width: uiPrefs.w[c.key], minWidth: uiPrefs.w[c.key], maxWidth: uiPrefs.w[c.key] }
                      : { minWidth: '2rem', maxWidth: '40rem' }),
                  }}
                >
                  {c.label}
                  <ResizeHandle colKey={c.key} setW={setW} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((f) => (
              <tr
                key={f.id}
                className={cn('align-top', f.origen === 'atlas' && 'bg-brand-50/40')}
                title={
                  f.editadoPor
                    ? `${f.origen === 'atlas' ? 'Creado' : 'Editado'} en Atlas por ${f.editadoPor}`
                    : 'Importado del Sheet'
                }
              >
                {COLS.map((c) => {
                  const rowKey = `${f.origen}|${f.fila ?? f.id}`;
                  const miColor = colorPersonal(uiPrefs, rowKey, c.key);
                  return (
                  <td
                    key={c.key}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setMenuColor({ x: e.clientX, y: e.clientY, rowKey, colKey: c.key });
                    }}
                    className={cn(
                      'px-1.5 py-1 border-b border-r border-ink-100 whitespace-nowrap',
                      c.key === 'actividad' && 'max-w-[26rem] overflow-hidden whitespace-normal',
                      c.key === 'respuesta' && 'max-w-[20rem] overflow-hidden whitespace-normal',
                      c.key === 'cliente' && 'max-w-[14rem] overflow-hidden',
                    )}
                    style={{
                      ...(miColor ? { backgroundColor: miColor, color: '#1f2937' } : {}),
                      ...(uiPrefs.w[c.key] ? { maxWidth: uiPrefs.w[c.key], overflow: 'hidden' } : {}),
                    }}
                  >
                    <Celda fila={f} campo={c.key} editable={editable} />
                  </td>
                  );
                })}
              </tr>
            ))}
            {items.length === 0 && !q.isLoading && (
              <tr>
                <td colSpan={COLS.length} className="px-3 py-8 text-center text-ink-400">
                  Sin registros para esos filtros.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {menuColor && (
        <ColorMenu
          menu={menuColor}
          onPick={(scope, color) => setUiPrefs((p) => aplicarColor(p, menuColor, scope, color))}
          onClose={() => setMenuColor(null)}
        />
      )}

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Select
          value={String(perPage)}
          onValueChange={(v) => {
            setPerPage(Number(v));
            setPage(1);
          }}
        >
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[50, 100, 200, 500].map((n) => (
              <SelectItem key={n} value={String(n)}>
                {n} por página
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {totalPages > 1 && (
          <>
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(1)} title="Primera página">
              «
            </Button>
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              ‹
            </Button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((n) => n === 1 || n === totalPages || Math.abs(n - page) <= 2)
              .map((n, idx, arr) => (
                <span key={n} className="flex items-center gap-1">
                  {idx > 0 && arr[idx - 1] !== n - 1 && <span className="text-ink-300">…</span>}
                  <Button variant={n === page ? 'default' : 'outline'} size="sm" onClick={() => setPage(n)}>
                    {n}
                  </Button>
                </span>
              ))}
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
              ›
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(totalPages)} title="Última página">
              »
            </Button>
          </>
        )}
        <span className="text-ink-400 ml-1">
          {q.data ? `${q.data.total} registros · página ${page} de ${totalPages}` : ''}
        </span>
      </div>
    </div>
  );
}
